<script setup lang="ts">
import { Icon } from '@iconify/vue';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

const { t, tQualityLabel } = useI18n();

type Digest = {
  lowRiskReady?: number;
  highRisk?: number;
  mediumRisk?: number;
  agingCount?: number;
  groupReady?: number;
  dupGroups?: number;
  difference?: number;
  pctMatched?: number;
  message?: string;
};

type Alerts = {
  qualityScore?: number;
  qualityLabel?: string;
  gapCount?: number;
  criticalFindings?: number;
  reconPending?: number;
  missingNpwp?: string[];
  taxDeadlines?: {
    overdueCount?: number;
    h3Count?: number;
    h7Count?: number;
    tasks?: Array<{
      id: string;
      severity: string;
      daysLeft: number;
      title: string;
      date: string;
      bucket: string;
      kind?: string;
      to: string;
      action?: string;
      actionLabel?: string;
      secondaryTo?: string;
      secondaryLabel?: string;
    }>;
  };
};

type ReconTaskApi = {
  id: string;
  suggestionId?: string | null;
  kind?: string;
  risk?: string;
  amount?: number;
  count?: number;
  label: string;
  detail: string;
  companyCode?: string | null;
  companyId?: number | null;
  to: string;
};

type TaskRow = {
  id: string;
  section: 'recon' | 'quality' | 'hr' | 'tax' | 'reminder';
  label: string;
  detail: string;
  count: number;
  tone: 'success' | 'warning' | 'error' | 'info' | 'neutral';
  to: string;
  secondaryTo?: string;
  secondaryLabel?: string;
  actionLabel?: string;
  notifId?: number;
  reviewable?: boolean;
};

const digest = ref<Digest | null>(null);
const alerts = ref<Alerts | null>(null);
const reconTasks = ref<ReconTaskApi[]>([]);
const pendingCount = ref(0);
const hrUnread = ref(0);
const hrPendingKasbon = ref(0);
const hrPendingKasbonNames = ref<string[]>([]);
const hrDataGaps = ref<{
  completenessPct: number;
  incompleteCount: number;
  missingNikLabel: string;
  missingNpwpLabel: string;
  incompleteLabel: string;
  missingNik: Array<{ id: number; name: string; companyCode: string | null; gaps: string[] }>;
  missingNpwp: Array<{ id: number; name: string; companyCode: string | null; gaps: string[] }>;
  incomplete: Array<{ id: number; name: string; companyCode: string | null; gaps: string[] }>;
} | null>(null);
const hrItems = ref<
  Array<{
    id: number;
    title: string;
    body: string | null;
    link: string | null;
    kind: string;
    isRead: boolean;
  }>
>([]);
const reviewingId = ref<number | null>(null);
const reminderItems = ref<
  Array<{
    id: number;
    title: string;
    body: string | null;
    status: string;
    occurrenceDate: string;
    deadlineDate: string | null;
    notifiable: boolean;
  }>
>([]);
const open = ref(false);
const loading = ref(false);
let pollTimer: number | undefined;
const { mode: deviceMode, CHANGE_EVENT } = useDevicePreview();

const digestWidth = computed(() => {
  if (!import.meta.client) return 420;
  const frameW =
    deviceMode.value === 'hp' ? 390 : deviceMode.value === 'tablet' ? 768 : window.innerWidth;
  return Math.min(420, Math.max(260, frameW - 24));
});

const router = useRouter();

function onDevicePreviewChange() {
  open.value = false;
}

function riskTone(risk?: string): TaskRow['tone'] {
  if (risk === 'high') return 'error';
  if (risk === 'medium' || risk === 'med') return 'warning';
  if (risk === 'low') return 'success';
  return 'info';
}

function formatRpShort(n: number) {
  if (!Number.isFinite(n) || n === 0) return '';
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(n);
}

async function refresh() {
  loading.value = true;
  try {
    const [recon, alertRes, hrRes, reminderRes] = await Promise.all([
      $fetch<{
        pendingCount: number;
        digest?: Digest;
        agingCount?: number;
        highRisk?: number;
        mediumRisk?: number;
        lowRisk?: number;
        tasks?: ReconTaskApi[];
      }>('/api/reconciliation/count'),
      $fetch<Alerts>('/api/dashboard/alerts').catch(() => null),
      $fetch<{
        items: Array<{
          id: number;
          title: string;
          body: string | null;
          link: string | null;
          kind: string;
          isRead: boolean;
        }>;
        unread: number;
        pendingKasbon: number;
        pendingKasbonNames?: string[];
        dataGaps?: {
          completenessPct: number;
          incompleteCount: number;
          missingNikLabel: string;
          missingNpwpLabel: string;
          incompleteLabel: string;
          missingNik: Array<{ id: number; name: string; companyCode: string | null; gaps: string[] }>;
          missingNpwp: Array<{ id: number; name: string; companyCode: string | null; gaps: string[] }>;
          incomplete: Array<{ id: number; name: string; companyCode: string | null; gaps: string[] }>;
        };
      }>('/api/hr/notifications').catch(() => null),
      $fetch<{
        items: Array<{
          id: number;
          title: string;
          body: string | null;
          status: string;
          occurrenceDate: string;
          deadlineDate: string | null;
          notifiable: boolean;
        }>;
      }>('/api/reminders').catch(() => null),
    ]);
    pendingCount.value = Number(recon.pendingCount) || 0;
    reconTasks.value = Array.isArray(recon.tasks) ? recon.tasks : [];
    digest.value = recon.digest || {
      message: pendingCount.value
        ? t('notif.reconWaiting', { n: pendingCount.value })
        : t('notif.reconSafeShort'),
      lowRiskReady: recon.lowRisk || 0,
      mediumRisk: recon.mediumRisk || 0,
      agingCount: recon.agingCount || 0,
      highRisk: recon.highRisk || 0,
      groupReady: 0,
      dupGroups: 0,
      pctMatched: 0,
    };
    alerts.value = alertRes;
    if (hrRes) {
      hrUnread.value = Number(hrRes.unread) || 0;
      hrPendingKasbon.value = Number(hrRes.pendingKasbon) || 0;
      hrPendingKasbonNames.value = hrRes.pendingKasbonNames || [];
      hrDataGaps.value = hrRes.dataGaps || null;
      const rawItems = hrRes.items || [];
      const aiNotes = rawItems.filter((x) => String(x.kind || '').startsWith('ai_'));
      const other = rawItems.filter((x) => !String(x.kind || '').startsWith('ai_'));
      hrItems.value = [...aiNotes, ...other].slice(0, 16).map((x) => ({
        id: x.id,
        title: x.title,
        body: x.body,
        link: x.link,
        kind: x.kind,
        isRead: !!x.isRead,
      }));
    }
    reminderItems.value = (reminderRes?.items || []).filter((x) => x.notifiable).slice(0, 8);
  } catch {
    digest.value = null;
    reconTasks.value = [];
    pendingCount.value = 0;
    reminderItems.value = [];
  } finally {
    loading.value = false;
  }
}

function onReconEvent(e: Event) {
  const detail = (e as CustomEvent).detail;
  if (detail && typeof detail === 'object' && 'pendingCount' in detail) {
    pendingCount.value = Number(detail.pendingCount) || 0;
  }
  refresh();
}

function onAppNotify() {
  refresh();
}

onMounted(() => {
  refresh();
  window.addEventListener('recon-count', onReconEvent);
  window.addEventListener('app-notify', onAppNotify);
  window.addEventListener(CHANGE_EVENT, onDevicePreviewChange);
  pollTimer = window.setInterval(refresh, 60000);
});
onUnmounted(() => {
  window.removeEventListener('recon-count', onReconEvent);
  window.removeEventListener('app-notify', onAppNotify);
  window.removeEventListener(CHANGE_EVENT, onDevicePreviewChange);
  if (pollTimer) window.clearInterval(pollTimer);
});

watch(open, (v) => {
  if (v) refresh();
});

/** Prefer per-task rows from API; fall back to category aggregates. */
const reconRows = computed<TaskRow[]>(() => {
  if (reconTasks.value.length) {
    return reconTasks.value.map((task) => {
      const amt = formatRpShort(Number(task.amount) || 0);
      const detailParts = [task.detail, amt].filter(Boolean);
      return {
        id: `${task.companyId || 'x'}-${task.id}`,
        section: 'recon' as const,
        label: task.label,
        detail: detailParts.join(' · '),
        count: Number(task.count) > 0 ? Number(task.count) : 1,
        tone: riskTone(task.risk),
        to: task.to || '/reconciliation',
      };
    });
  }

  const d = digest.value;
  if (!d) return [];
  const rows: TaskRow[] = [];
  const push = (row: TaskRow) => {
    if (row.count > 0) rows.push(row);
  };
  push({
    id: 'high',
    section: 'recon',
    label: t('notif.highRisk'),
    detail: t('notif.highRiskDetail'),
    count: Number(d.highRisk) || 0,
    tone: 'error',
    to: '/reconciliation?focus=high',
  });
  push({
    id: 'med',
    section: 'recon',
    label: t('notif.medRisk'),
    detail: t('notif.medRiskDetail'),
    count: Number(d.mediumRisk) || 0,
    tone: 'warning',
    to: '/reconciliation?focus=med',
  });
  push({
    id: 'low',
    section: 'recon',
    label: t('notif.lowRisk'),
    detail: t('notif.lowRiskDetail'),
    count: Number(d.lowRiskReady) || 0,
    tone: 'success',
    to: '/reconciliation?focus=low',
  });
  push({
    id: 'aging',
    section: 'recon',
    label: t('notif.aging'),
    detail: t('notif.agingDetail'),
    count: Number(d.agingCount) || 0,
    tone: 'error',
    to: '/reconciliation?focus=aging',
  });
  push({
    id: 'dup',
    section: 'recon',
    label: t('notif.dup'),
    detail: t('notif.dupDetail'),
    count: Number(d.dupGroups) || 0,
    tone: 'warning',
    to: '/reconciliation?focus=dup',
  });
  push({
    id: 'group',
    section: 'recon',
    label: t('notif.group'),
    detail: t('notif.groupDetail'),
    count: Number(d.groupReady) || 0,
    tone: 'info',
    to: '/reconciliation?focus=group',
  });
  if (!rows.length && pendingCount.value > 0) {
    rows.push({
      id: 'pending',
      section: 'recon',
      label: t('notif.pendingTasks'),
      detail: t('notif.pendingDetail'),
      count: pendingCount.value,
      tone: 'info',
      to: '/reconciliation?focus=pending',
    });
  }
  return rows;
});

const qualityRows = computed<TaskRow[]>(() => {
  const a = alerts.value;
  const rows: TaskRow[] = [];
  if (a) {
    const missing = a.missingNpwp || [];
    if (missing.length) {
      rows.push({
        id: 'npwp',
        section: 'quality',
        label: t('notif.npwpEmpty'),
        detail: missing.join(', '),
        count: missing.length,
        tone: 'error',
        to: '/companies',
      });
    }
    if ((a.criticalFindings || 0) > 0) {
      rows.push({
        id: 'critical',
        section: 'quality',
        label: t('notif.critical'),
        detail: t('notif.criticalDetail'),
        count: Number(a.criticalFindings) || 0,
        tone: 'error',
        to: '/control/quality',
      });
    }
    if ((a.gapCount || 0) > 0) {
      rows.push({
        id: 'gap',
        section: 'quality',
        label: t('notif.gap'),
        detail: t('notif.gapDetail'),
        count: Number(a.gapCount) || 0,
        tone: 'warning',
        to: '/control/quality',
      });
    }
    const score = Number(a.qualityScore);
    const label = tQualityLabel(a.qualityLabel);
    if (
      Number.isFinite(score) &&
      a.qualityLabel &&
      !/siap|ready/i.test(String(a.qualityLabel)) &&
      score < 80
    ) {
      rows.push({
        id: 'score',
        section: 'quality',
        label: t('notif.scoreLabel', { score }),
        detail: label,
        count: 1,
        tone: score < 50 ? 'error' : 'warning',
        to: '/control/quality',
      });
    }
  }
  // Note AI auto-eksekusi (unread) — cross-check admin di lonceng
  for (const item of hrItems.value.filter(
    (x) => String(x.kind || '').startsWith('ai_') && !x.isRead
  )) {
    rows.push({
      id: `ai-${item.id}`,
      section: 'quality',
      label: item.title,
      detail: item.body || t('notif.aiConsDetail'),
      count: 1,
      tone: item.kind.includes('_ok')
        ? 'success'
        : item.kind.includes('gap') || item.kind.includes('critical') || item.kind.includes('score')
          ? 'warning'
          : 'info',
      to: item.link || '/control/quality',
      notifId: item.id,
      reviewable: true,
      actionLabel: t('notif.markReviewed'),
    });
  }
  return rows;
});

const taxRows = computed<TaskRow[]>(() => {
  const tasks = alerts.value?.taxDeadlines?.tasks || [];
  return tasks.slice(0, 8).map((task) => {
    const urgency =
      task.bucket === 'overdue'
        ? t('notif.taxOverdue')
        : task.bucket === 'h3'
          ? t('notif.taxH3')
          : t('notif.taxH7');
    const action = task.actionLabel || 'Buka';
    return {
      id: task.id,
      section: 'tax' as const,
      label: `${action} · ${urgency}`,
      detail: `${task.date} · ${task.title} (${task.daysLeft}h)`,
      count: 1,
      tone:
        task.bucket === 'overdue' ? 'error' : task.bucket === 'h3' ? 'warning' : 'info',
      to: task.to || '/tax/calendar',
      actionLabel: task.actionLabel,
      secondaryTo: task.secondaryTo,
      secondaryLabel: task.secondaryLabel,
    };
  });
});

const reminderRows = computed<TaskRow[]>(() =>
  reminderItems.value.map((item) => ({
    id: `rem-${item.id}`,
    section: 'reminder' as const,
    label: item.title,
    detail: [
      item.body || '',
      `Reminder ${item.occurrenceDate}`,
      item.deadlineDate ? `Deadline ${item.deadlineDate}` : '',
    ]
      .filter(Boolean)
      .join(' · '),
    count: 1,
    tone: item.status === 'proses' ? 'warning' : 'info',
    to: '/reminders',
  }))
);

/** Badge = jumlah tugas notif (per task) + quality alerts + HR + pajak */
const alertCategoryCount = computed(
  () =>
    reconRows.value.length +
    reminderRows.value.length +
    qualityRows.value.length +
    taxRows.value.length +
    (hrPendingKasbon.value > 0 ? 1 : 0) +
    (hrDataGaps.value?.incompleteCount ? 1 : 0) +
    (hrDataGaps.value?.missingNpwp?.length ? 1 : 0) +
    (hrDataGaps.value?.missingNik?.length ? 1 : 0) +
    Math.min(hrUnread.value, 5)
);

const badgeLabel = computed(() => {
  const n = alertCategoryCount.value;
  if (n <= 0) return '';
  return n > 99 ? '99+' : String(n);
});

const hrRows = computed<TaskRow[]>(() => {
  const rows: TaskRow[] = [];
  const gaps = hrDataGaps.value;
  if (gaps?.incompleteCount) {
    rows.push({
      id: 'hr-incomplete',
      section: 'hr',
      label: t('notif.hrIncomplete', { n: gaps.incompleteCount, pct: gaps.completenessPct }),
      detail: gaps.incompleteLabel || t('notif.hrIncompleteDetail'),
      count: gaps.incompleteCount,
      tone: 'warning',
      to: '/hr/employees?gap=npwp',
    });
  }
  if (gaps?.missingNpwp?.length) {
    rows.push({
      id: 'hr-npwp',
      section: 'hr',
      label: t('notif.hrMissingNpwp', { n: gaps.missingNpwp.length }),
      detail: gaps.missingNpwpLabel || t('notif.hrMissingNpwpDetail'),
      count: gaps.missingNpwp.length,
      tone: 'error',
      to: '/hr/employees?gap=npwp',
    });
  }
  if (gaps?.missingNik?.length) {
    rows.push({
      id: 'hr-nik',
      section: 'hr',
      label: t('notif.hrMissingNik', { n: gaps.missingNik.length }),
      detail: gaps.missingNikLabel || t('notif.hrMissingNikDetail'),
      count: gaps.missingNik.length,
      tone: 'warning',
      to: '/hr/employees?gap=nik',
    });
  }
  if (hrPendingKasbon.value > 0) {
    rows.push({
      id: 'kasbon-pending',
      section: 'hr',
      label: t('notif.kasbonPending'),
      detail:
        hrPendingKasbonNames.value.length > 0
          ? hrPendingKasbonNames.value.join(', ')
          : t('notif.kasbonPendingDetail'),
      count: hrPendingKasbon.value,
      tone: 'warning',
      to: '/hr/employees?tab=kasbon',
    });
  }
  for (const item of hrItems.value
    .filter((x) => !String(x.kind || '').startsWith('ai_'))
    .slice(0, 5)) {
    rows.push({
      id: `n-${item.id}`,
      section: 'hr',
      label: item.title,
      detail: item.body || '',
      count: 1,
      tone: item.kind.includes('reject')
        ? 'error'
        : item.kind.includes('approve')
          ? 'success'
          : 'info',
      to: item.link || '/hr/me',
    });
  }
  return rows;
});

const pctMatched = computed(() => Number(digest.value?.pctMatched) || 0);
const qualityScore = computed(() => Number(alerts.value?.qualityScore) || 0);
const qualityLabel = computed(() => tQualityLabel(alerts.value?.qualityLabel));
const hasMissingNpwp = computed(() => (alerts.value?.missingNpwp || []).length > 0);
const hasReconWork = computed(
  () => pendingCount.value > 0 || (Number(digest.value?.agingCount) || 0) > 0 || reconRows.value.length > 0
);

function toneClass(tone: TaskRow['tone']) {
  return `tone-${tone}`;
}

function go(row: TaskRow) {
  open.value = false;
  router.push(row.to);
}

function goPath(path: string) {
  open.value = false;
  router.push(path);
}

async function markReviewed(row: TaskRow) {
  if (!row.notifId || reviewingId.value) return;
  reviewingId.value = row.notifId;
  try {
    await $fetch('/api/hr/notifications', {
      method: 'POST',
      body: { action: 'review', ids: [row.notifId] },
    });
    hrItems.value = hrItems.value.map((x) =>
      x.id === row.notifId ? { ...x, isRead: true } : x
    );
    hrUnread.value = Math.max(0, hrUnread.value - 1);
    window.dispatchEvent(new CustomEvent('app-notify', { detail: { source: 'review' } }));
  } catch {
    /* ignore */
  } finally {
    reviewingId.value = null;
  }
}

async function markAllAiReviewed() {
  const ids = qualityRows.value.filter((r) => r.reviewable && r.notifId).map((r) => r.notifId!);
  if (!ids.length || reviewingId.value) return;
  reviewingId.value = ids[0];
  try {
    await $fetch('/api/hr/notifications', {
      method: 'POST',
      body: { action: 'review', ids },
    });
    const idSet = new Set(ids);
    hrItems.value = hrItems.value.map((x) => (idSet.has(x.id) ? { ...x, isRead: true } : x));
    await refresh();
  } catch {
    /* ignore */
  } finally {
    reviewingId.value = null;
  }
}
</script>

<template>
  <v-menu
    v-model="open"
    :close-on-content-click="false"
    location="bottom end"
    :max-width="digestWidth"
  >
    <template #activator="{ props }">
      <v-tooltip :text="t('notif.tooltip')" location="bottom">
        <template #activator="{ props: tip }">
          <v-btn
            icon
            class="custom-hover-primary notif-trigger"
            size="small"
            variant="text"
            rounded="circle"
            color="primary"
            v-bind="{ ...props, ...tip }"
          >
            <v-badge
              class="notif-trigger__badge"
              :content="badgeLabel"
              :model-value="!!badgeLabel"
              color="error"
              floating
              offset-x="2"
              offset-y="7"
            >
              <Icon icon="solar:bell-bing-line-duotone" height="20" />
            </v-badge>
          </v-btn>
        </template>
      </v-tooltip>
    </template>

    <v-sheet
      class="digest-sheet mt-2"
      :width="digestWidth"
      elevation="12"
      rounded="xl"
    >
      <div class="digest-head">
        <div>
          <div class="digest-kicker">{{ t('notif.kicker') }}</div>
          <div class="digest-title">
            {{ t('notif.title') }}
            <span v-if="alertCategoryCount" class="digest-badge">{{
              t('notif.alertCount', { n: alertCategoryCount })
            }}</span>
          </div>
        </div>
        <div class="digest-tools">
          <v-tooltip :text="t('notif.reload')" location="bottom">
            <template #activator="{ props: tip }">
              <v-btn
                v-bind="tip"
                icon
                size="x-small"
                variant="tonal"
                color="primary"
                :loading="loading"
                @click="refresh"
              >
                <v-icon size="16">mdi-refresh</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
          <v-tooltip :text="t('common.close')" location="bottom">
            <template #activator="{ props: tip }">
              <v-btn v-bind="tip" icon size="x-small" variant="text" @click="open = false">
                <v-icon size="16">mdi-close</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </div>

      <div class="digest-meta">
        <div class="digest-meta__item">
          <span class="label">{{ t('notif.matched') }}</span>
          <strong>{{ pctMatched }}%</strong>
        </div>
        <div class="digest-meta__item">
          <span class="label">{{ t('notif.pendingMatch') }}</span>
          <strong>{{ pendingCount }}</strong>
        </div>
        <div class="digest-meta__item">
          <span class="label">{{ t('notif.dataScore') }}</span>
          <strong>{{ qualityScore || '—' }}</strong>
          <span class="meta-sub">{{ qualityLabel }}</span>
        </div>
      </div>

      <div v-if="reminderRows.length" class="digest-section">
        <div class="digest-section__head">
          <span class="digest-section__label">Reminder</span>
          <span class="digest-section__line" aria-hidden="true" />
        </div>
        <div class="digest-table-wrap">
          <table class="digest-table">
            <thead>
              <tr>
                <th class="col-no">No</th>
                <th>{{ t('notif.task') }}</th>
                <th class="text-end">{{ t('notif.count') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, i) in reminderRows"
                :key="row.id"
                class="row-click"
                :class="toneClass(row.tone)"
                @click="go(row)"
              >
                <td class="col-no">
                  <span class="no-pill">{{ i + 1 }}</span>
                </td>
                <td>
                  <div class="task-label">{{ row.label }}</div>
                  <div class="task-detail">{{ row.detail }}</div>
                </td>
                <td class="text-end">
                  <span class="count-chip" :class="toneClass(row.tone)">{{ row.count }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- REKONSILIASI — per task -->
      <div class="digest-section digest-section--scroll">
        <div class="digest-section__head">
          <span class="digest-section__label">{{ t('notif.recon') }}</span>
          <span class="digest-section__line" aria-hidden="true" />
        </div>
        <div class="digest-table-wrap">
          <table class="digest-table">
            <thead>
              <tr>
                <th class="col-no">No</th>
                <th>{{ t('notif.task') }}</th>
                <th class="text-end">{{ t('notif.count') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, i) in reconRows"
                :key="row.id"
                class="row-click"
                :class="toneClass(row.tone)"
                @click="go(row)"
              >
                <td class="col-no">
                  <span class="no-pill">{{ i + 1 }}</span>
                </td>
                <td>
                  <div class="task-label">{{ row.label }}</div>
                  <div class="task-detail">{{ row.detail }}</div>
                </td>
                <td class="text-end">
                  <span class="count-chip" :class="toneClass(row.tone)">{{ row.count }}</span>
                </td>
              </tr>
              <tr v-if="!reconRows.length">
                <td colspan="3" class="empty-row">{{ t('notif.reconSafe') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- KELENGKAPAN + NOTE AI -->
      <div class="digest-section">
        <div class="digest-section__head">
          <span class="digest-section__label">{{ t('notif.quality') }}</span>
          <span class="digest-section__line" aria-hidden="true" />
          <button
            v-if="qualityRows.some((r) => r.reviewable)"
            type="button"
            class="digest-section__review-all"
            :disabled="!!reviewingId"
            @click="markAllAiReviewed"
          >
            {{ t('notif.markAllReviewed') }}
          </button>
        </div>
        <div class="digest-table-wrap">
          <table class="digest-table">
            <thead>
              <tr>
                <th class="col-no">No</th>
                <th>{{ t('notif.alert') }}</th>
                <th class="text-end">{{ t('notif.count') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, i) in qualityRows"
                :key="row.id"
                class="row-click"
                :class="toneClass(row.tone)"
              >
                <td class="col-no" @click="go(row)">
                  <span class="no-pill">{{ i + 1 }}</span>
                </td>
                <td>
                  <div class="task-label" @click="go(row)">{{ row.label }}</div>
                  <div class="task-detail" @click="go(row)">{{ row.detail }}</div>
                  <div v-if="row.reviewable" class="tax-actions">
                    <button
                      type="button"
                      class="tax-act"
                      :disabled="reviewingId === row.notifId"
                      @click.stop="markReviewed(row)"
                    >
                      {{ t('notif.markReviewed') }}
                    </button>
                    <button type="button" class="tax-act tax-act--sec" @click.stop="go(row)">
                      {{ t('notif.openLink') }}
                    </button>
                  </div>
                </td>
                <td class="text-end" @click="go(row)">
                  <span class="count-chip" :class="toneClass(row.tone)">{{ row.count }}</span>
                </td>
              </tr>
              <tr v-if="!qualityRows.length">
                <td colspan="3" class="empty-row">{{ t('notif.qualityOk') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- BATAS SETOR PAJAK -->
      <div v-if="taxRows.length" class="digest-section">
        <div class="digest-section__head">
          <span class="digest-section__label">{{ t('notif.tax') }}</span>
          <span class="digest-section__line" aria-hidden="true" />
        </div>
        <div class="digest-table-wrap">
          <table class="digest-table">
            <thead>
              <tr>
                <th class="col-no">No</th>
                <th>{{ t('notif.task') }}</th>
                <th class="text-end">{{ t('notif.count') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, i) in taxRows"
                :key="row.id"
                class="row-click"
                :class="toneClass(row.tone)"
              >
                <td class="col-no" @click="go(row)">
                  <span class="no-pill">{{ i + 1 }}</span>
                </td>
                <td>
                  <div class="task-label" @click="go(row)">{{ row.label }}</div>
                  <div class="task-detail" @click="go(row)">{{ row.detail }}</div>
                  <div class="tax-actions">
                    <button type="button" class="tax-act" @click.stop="go(row)">
                      {{ row.actionLabel || 'Buka' }}
                    </button>
                    <button
                      v-if="row.secondaryTo"
                      type="button"
                      class="tax-act tax-act--sec"
                      @click.stop="goPath(row.secondaryTo)"
                    >
                      {{ row.secondaryLabel || 'PPh' }}
                    </button>
                  </div>
                </td>
                <td class="text-end" @click="go(row)">
                  <span class="count-chip" :class="toneClass(row.tone)">{{ row.count }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- HR / KASBON / DATA BELUM LENGKAP -->
      <div class="digest-section">
        <div class="digest-section__head">
          <span class="digest-section__label">{{ t('notif.hr') }}</span>
          <span class="digest-section__line" aria-hidden="true" />
        </div>
        <div class="digest-table-wrap">
          <table class="digest-table">
            <thead>
              <tr>
                <th class="col-no">No</th>
                <th>{{ t('notif.task') }}</th>
                <th class="text-end">{{ t('notif.count') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, i) in hrRows"
                :key="row.id"
                class="row-click"
                :class="toneClass(row.tone)"
                @click="go(row)"
              >
                <td class="col-no">
                  <span class="no-pill">{{ i + 1 }}</span>
                </td>
                <td>
                  <div class="task-label">{{ row.label }}</div>
                  <div class="task-detail">{{ row.detail }}</div>
                </td>
                <td class="text-end">
                  <span class="count-chip" :class="toneClass(row.tone)">{{ row.count }}</span>
                </td>
              </tr>
              <tr v-if="!hrRows.length">
                <td colspan="3" class="empty-row">{{ t('notif.hrOk') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="digest-foot">
        <v-btn
          v-if="hasMissingNpwp"
          block
          variant="tonal"
          color="error"
          rounded="lg"
          class="mb-2"
          @click="goPath('/companies')"
        >
          {{ t('notif.fillNpwp') }}
        </v-btn>
        <v-btn
          v-if="hasReconWork"
          block
          color="primary"
          variant="flat"
          rounded="lg"
          @click="goPath('/reconciliation')"
        >
          {{ t('notif.openRecon') }}
        </v-btn>
        <v-btn
          v-else-if="!hasMissingNpwp"
          block
          variant="tonal"
          rounded="lg"
          @click="goPath('/control/quality')"
        >
          {{ t('notif.checkQuality') }}
        </v-btn>
      </div>
    </v-sheet>
  </v-menu>
</template>

<style scoped>
.notif-trigger {
  overflow: visible !important;
  contain: none !important;
}
.notif-trigger :deep(.v-btn__content),
.notif-trigger__badge,
.notif-trigger__badge :deep(.v-badge),
.notif-trigger__badge :deep(.v-badge__wrapper) {
  overflow: visible !important;
  contain: none !important;
}
.notif-trigger__badge :deep(.v-badge__badge) {
  z-index: 2;
}
.digest-sheet {
  background: #fff;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  max-height: min(82vh, 640px);
  display: flex;
  flex-direction: column;
}
.digest-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px 10px;
  border-bottom: 1px solid #eef2f7;
  flex-shrink: 0;
}
.digest-kicker {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #64748b;
}
.digest-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.digest-badge {
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: #e11d48;
  padding: 2px 8px;
  border-radius: 999px;
}
.digest-tools {
  display: flex;
  align-items: center;
  gap: 4px;
}
.digest-meta {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  padding: 12px 16px;
  flex-shrink: 0;
}
@media (max-width: 420px) {
  .digest-meta {
    grid-template-columns: 1fr;
  }
  .digest-sheet {
    max-width: calc(100vw - 16px);
  }
}
.digest-meta__item {
  background: #f8fafc;
  border: 1px solid #e8ecf2;
  border-radius: 12px;
  padding: 10px 12px;
}
.digest-meta__item .label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 2px;
}
.digest-meta__item strong {
  font-size: 16px;
  color: #0f172a;
}
.meta-sub {
  display: block;
  font-size: 10px;
  color: #64748b;
  margin-top: 2px;
  line-height: 1.2;
}
.digest-section {
  padding: 0 12px 10px;
}
.digest-section--scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
}
.digest-section__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 4px 4px 8px;
}
.digest-section__review-all {
  margin-left: auto;
  border: 1px solid #99f6e4;
  background: #f0fdfa;
  color: #0f766e;
  font-size: 10px;
  font-weight: 800;
  padding: 3px 8px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
}
.digest-section__review-all:disabled {
  opacity: 0.55;
  cursor: default;
}
.digest-section__label {
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #64748b;
}
.digest-section__line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, #cbd5e1 0%, transparent 100%);
}
.digest-table-wrap {
  border: 1px solid #e8ecf2;
  border-radius: 12px;
  overflow: hidden;
}
.digest-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.digest-table th {
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
  background: #f1f5f9;
  padding: 8px 10px;
  border-bottom: 1px solid #e2e8f0;
}
.digest-table td {
  padding: 10px;
  border-bottom: 1px solid #eef2f7;
  vertical-align: top;
  color: #0f172a;
}
.digest-table tbody tr:last-child td {
  border-bottom: none;
}
.row-click {
  cursor: pointer;
  transition: background 0.12s ease;
}
.row-click:hover {
  background: #f1f5f9;
}
.col-no {
  width: 42px;
}
.no-pill {
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #e2e8f0;
  font-size: 11px;
  font-weight: 800;
  color: #334155;
}
.task-label {
  font-weight: 700;
  line-height: 1.25;
}
.task-detail {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}
.tax-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}
.tax-act {
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #0f172a;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  cursor: pointer;
}
.tax-act:hover {
  background: #e2e8f0;
}
.tax-act--sec {
  border-color: #86efac;
  background: #f0fdf4;
  color: #166534;
}
.count-chip {
  display: inline-block;
  min-width: 28px;
  text-align: center;
  font-weight: 800;
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 8px;
  background: #e2e8f0;
  color: #334155;
}
.count-chip.tone-success,
tr.tone-success .no-pill {
  background: #dcfce7;
  color: #166534;
}
.count-chip.tone-warning,
tr.tone-warning .no-pill {
  background: #ffedd5;
  color: #9a3412;
}
.count-chip.tone-error,
tr.tone-error .no-pill {
  background: #ffe4e6;
  color: #9f1239;
}
.count-chip.tone-info,
tr.tone-info .no-pill {
  background: #e0e7ff;
  color: #3730a3;
}
.empty-row {
  text-align: center;
  color: #64748b !important;
  padding: 16px 12px !important;
}
.digest-foot {
  padding: 10px 16px 16px;
  border-top: 1px solid #eef2f7;
  flex-shrink: 0;
  margin-top: auto;
}
</style>
